import styled from '@emotion/styled'
import { AnimatedImage } from 'app/components/Common/Animation/AnimatedImage'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { styles } from 'app/theme'
import { useVocabularyData } from 'app/utils/vocabulary'
import { uniq } from 'lodash'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

export interface Props {
  facebookURL?: string
  images?: ImageProps[]
  instagramURL?: string
  languageCode: string
  title?: string
}

export const SocialWall = memo(function SocialWall({
  facebookURL,
  images,
  instagramURL,
  languageCode,
  title,
}: Props) {
  if (!images) {
    return null
  }

  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  return (
    <Container ref={ref}>
      <Head>
        <Label tabIndex={0}>{useVocabularyData('social', languageCode)}</Label>

        {title ? <Title text={title} /> : null}

        {facebookURL || instagramURL ? (
          <Buttons>
            {facebookURL ? (
              <Anchor
                ariaLabel="Button to open Facebook page"
                label="Facebook"
                rel="noopener"
                target="_blank"
                URL={facebookURL}
                variant="flat"
              />
            ) : null}

            {instagramURL ? (
              <Anchor
                ariaLabel="Button to open Instagram page"
                label="Instagram"
                rel="noopener"
                target="_blank"
                URL={instagramURL}
                variant="flat"
              />
            ) : null}
          </Buttons>
        ) : null}
      </Head>

      <Items>
        {uniq(images)
          .slice(0, 8)
          .map((item, index) => (
            <Item delay={index * 0.15} key={index}>
              <Image decorative {...item} />
            </Item>
          ))}
      </Items>
    </Container>
  )
})

const Container = styled.section`
  margin-top: 9.75rem;
  padding-bottom: 4.1875rem;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    padding-bottom: 2.625rem;
  }
`

const Head = styled.div`
  max-width: 53rem;
  margin: auto;
  text-align: center;

  @media (max-width: 1199px) {
    max-width: none;
    padding: 0 2rem;
  }
`

const Label = styled.div`
  ${styles.label}
`

const Title = styled(AnimatedTitle)`
  ${styles.claim}
`

const Buttons = styled(FadeInUp)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
`

const Anchor = styled(Button)`
  margin: 0 0.75rem;
`

const Items = styled.div`
  display: grid;
  grid-gap: 3.333vw;
  grid-template-columns: repeat(4, 1fr);
  grid-auto-rows: 20vw 3.333vw 20vw;
  grid-auto-flow: dense;
  margin-top: 5.25rem;
  padding: 0 5vw;

  > div {
    &:nth-of-type(4n + 1),
    &:nth-of-type(8n + 3),
    &:nth-of-type(8n + 6) {
      grid-row: span 2;
    }
  }

  @media (max-width: 1199px) {
    grid-gap: 2rem;
    grid-template-columns: repeat(2, 1fr);
    grid-auto-rows: 10.4375rem 1.4375rem 10.4375rem;
    margin-top: 3.75rem;
    padding: 0 2rem;

    > div {
      display: none;
      &:nth-of-type(-n + 4) {
        display: block;
      }
    }
  }
`

const Item = styled(AnimatedImage)`
  position: relative;

  img {
    ${styles.img}
  }
`
